import * as React from 'react';

import { useAppContext } from '../../../app/provider/useAppContext';
import { CartView, CTLineItem, MiniBasketItem, CTProduct } from "../types/cartTypes";
import { normalizeProducts } from '../utils/cartNormalization';
import { getCart, subscribeToCartUpdates, getProductsByIds } from "../utils/cartUtil";

export const useMiniBasketData = () => {
    const [items, setItems] = React.useState<MiniBasketItem[]>([]);
    const lastCartRef = React.useRef<CartView | null>(null);
    const addOnNamesRef = React.useRef<Record<string, string>>({});
    // Consume global context so currency/locale changes propagate immediately
    const [context] = useAppContext();

    /**
     * Maps a raw CT cart into display items.
     * Wrapped in useCallback so its reference only changes when the values it
     * reads from context (currency, locale) actually change — preventing
     * useEffect dependency loops.
     */
    const updateItemsFromCart = React.useCallback(async (cart: CartView | null) => {
        lastCartRef.current = cart;
        if (cart && cart.lineItems) {
            // 1. Identify IDs from attachedAddOns
            const allAddOnIds = new Set<string>();
            cart.lineItems.forEach((item: CTLineItem) => {
                if (item.custom?.fields?.attachedAddOns) {
                    item.custom.fields.attachedAddOns.forEach((id: string) => allAddOnIds.add(id));
                }
            });

            // 2. Fetch missing names
            const missingIds = Array.from(allAddOnIds).filter(id => !addOnNamesRef.current[id]);

            if (missingIds.length > 0) {
                const fetched = await getProductsByIds(missingIds);
                const products = normalizeProducts(fetched);
                products.forEach((p: CTProduct) => {
                    const data = p.masterData?.current || p.masterData?.staged || p;
                    const nameObj = data.name || p.name || {};
                    const name = nameObj[context?.locale || 'en'] || Object.values(nameObj)[0] || '';
                    addOnNamesRef.current[p.id] = name;
                });
            }

            const mappedItems: MiniBasketItem[] = cart.lineItems.map((item: CTLineItem) => {
                const priceObj = item.price.value;
                // Always favor selected currency for display; fall back to line-item currency if needed
                const currencyCode = context?.currency
                    || sessionStorage.getItem('currency')
                    || item.totalPrice?.currencyCode
                    || priceObj?.currencyCode
                    || 'USD';
                const passType = item.custom?.fields?.passType;

                let resolvedAddOns: string[] = [];
                if (item.custom?.fields?.attachedAddOns) {
                    resolvedAddOns = item.custom.fields.attachedAddOns.map((id: string) => addOnNamesRef.current[id]).filter(Boolean);
                }

                // Extract validity dates from attributes or custom fields
                const attributes = item.variant?.attributes || [];
                const getAttributeValue = (name: string): string | undefined => {
                    const attr = attributes.find((a) => a.name === name);
                    if (attr) return (Array.isArray(attr.value) ? attr.value[0] : attr.value) as string;
                    // Fallback to custom fields
                    return item.custom?.fields?.[name] as string | undefined;
                };

                const startDate = getAttributeValue('startDate');
                const endDate = getAttributeValue('endDate');

                const mapped: MiniBasketItem = {
                    id: item.id,
                    title: (item.name[context?.locale || "en"] || Object.values(item.name)[0] || "") + (passType ? ` ${passType}` : ""),
                    name: item.name, // Preserving raw localized name for MiniBasket grouping
                    quantity: item.quantity,
                    price: priceObj.centAmount / 100,
                    locale: context?.locale || sessionStorage.getItem('locale') || undefined,
                    currencyCode,
                    custom: item.custom,
                    variant: {
                      id: item.variant.id,
                      sku: item.variant.sku,
                      attributes: item.variant.attributes || []
                    },
                    totalPrice: item.totalPrice,
                    addOns: resolvedAddOns,
                    startDate,
                    endDate
                };
                return mapped;
            });

            setItems(mappedItems);

        } else {
            setItems([]);
        }
    }, [context?.currency, context?.locale]);

    React.useEffect(() => {
        const fetchCart = async () => {
            const cart = await getCart();
            updateItemsFromCart(cart);
        };

        fetchCart();

        // Subscribe to real-time updates
        const unsubscribe = subscribeToCartUpdates((cart) => {
            updateItemsFromCart(cart);
        });

        return () => unsubscribe();
    }, [updateItemsFromCart]);

    // Re-render items with latest context currency/locale without needing a reload.
    // updateItemsFromCart already changes when currency/locale changes (via useCallback
    // deps), which makes the effect above re-run automatically. This second effect
    // is kept as an explicit safety net for the case where the cart was already
    // loaded but context changed after the fact.
    React.useEffect(() => {
        if (lastCartRef.current) {
            updateItemsFromCart(lastCartRef.current);
        }
    }, [updateItemsFromCart]);

    return { items, cart: lastCartRef.current };
};
// Trigger rebuild Wed Apr 15 05:38:34 PM IST 2026
